import { SitemapEntry } from '../sitemap.types';

// This catalog is intentionally isolated from normal feature and release work.
// Agents may edit it only during an explicitly requested sitemap update run.
export const ADMIN_MANAGEMENT_SITEMAP_ENTRIES = [
	{
		id: 'find-organization-courses',
		title: 'Find organization courses',
		guidance:
			'Search the organization course list, use advanced filters, and open course information.',
		href: '/app/courses',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Courses', 'Organization'],
		searchTerms: [
			'course search',
			'advanced course search',
			'course information',
		],
	},
	{
		id: 'manage-program-kickoffs',
		title: 'Manage program kickoffs',
		guidance:
			'Review kickoff checklists and mark programs ready for review or ready for students.',
		href: '/app/organizations/kickoff',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Kickoff'],
		searchTerms: [
			'program checklist',
			'ready for review',
			'ready for students',
		],
	},
	{
		id: 'find-programs',
		title: 'Find a program',
		guidance:
			'Search programs and open their overview, administrator directory, or related management area.',
		href: '/app/program-management/search',
		application: 'admin',
		audiences: ['customer', 'admin', 'superAdmin'],
		categoryPath: ['Programs', 'Search'],
		searchTerms: [
			'program search',
			'school program',
			'institute search',
			'find program',
		],
	},
	{
		id: 'create-program',
		title: 'Create or copy a program',
		guidance:
			'Start a new program or copy an existing program as the basis for its configuration.',
		href: '/app/program-management/create-program',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Setup'],
		searchTerms: [
			'new program',
			'copy program',
			'clone program',
			'create school',
		],
	},
	{
		id: 'bulk-update-program-properties',
		title: 'Update properties for multiple programs',
		guidance:
			'Search for programs by property and apply a property update to the selected programs.',
		href: '/app/program-management/search',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Configuration'],
		searchTerms: [
			'bulk properties',
			'program property',
			'property filter',
			'bulk update',
		],
	},
	{
		id: 'configure-program',
		title: 'Configure a program',
		guidance:
			'Choose a program, then edit program-level settings and properties, including default pacing, final-exam bypass and auto-finalization rules, unlimited non-credit assignment excusals, service tiers when available, tags, links, and welcome-email sender or content.',
		href: '/app/program-management/overview',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Configuration'],
		searchTerms: [
			'program settings',
			'program properties',
			'program tags',
			'program default pacing',
			'program final exam bypass',
			'program auto finalization',
			'Unlimited Non-Credit Assignment Excusals',
			'program service tier',
			'welcome email',
			'welcome email sender',
		],
	},
	{
		id: 'manage-program-calendar',
		title: 'Manage a program calendar',
		guidance:
			'Choose a program to edit its calendar or export the calendar for use elsewhere.',
		href: '/app/program-management/overview',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Programs', 'Calendar'],
		searchTerms: [
			'program schedule',
			'program dates',
			'school calendar',
			'export calendar',
			'academic calendar',
		],
	},
	{
		id: 'manage-program-files',
		title: 'Manage program files',
		guidance:
			'Choose a program to upload or replace files, review file history, or expire an existing file.',
		href: '/app/program-management/overview',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Files'],
		searchTerms: [
			'program document',
			'upload file',
			'replace file',
			'file history',
			'expire file',
		],
	},
	{
		id: 'manage-program-administrators',
		title: 'Manage program administrators',
		guidance:
			'Add one or many administrators, copy or deactivate them, and manage their program access, student assignments, contact information, notification subscriptions, and welcome email.',
		href: '/app/program-management/admin-directory',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Administrators'],
		searchTerms: [
			'admin directory',
			'program staff',
			'administrator assignment',
			'school admin',
			'bulk add administrators',
			'program gatekeeper',
			'move administrator access',
			'admin notifications',
		],
	},
	{
		id: 'request-program-update',
		title: 'Request a program update',
		guidance:
			'Open Program Management and submit a requested change for a program you cannot edit directly.',
		href: '/app/program-management/overview',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Programs', 'Edit Requests'],
		searchTerms: [
			'request change',
			'program correction',
			'request edit',
			'update request',
		],
	},
	{
		id: 'review-program-edit-requests',
		title: 'Review program edit requests',
		guidance:
			'Create, review, complete, or cancel requests to change program information.',
		href: '/app/program-management/edit-requests',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Edit Requests'],
		searchTerms: [
			'program request',
			'pending edits',
			'complete request',
			'cancel request',
		],
	},
	{
		id: 'review-program-edit-history',
		title: 'Review program edit history',
		guidance:
			'Review changes made to programs and open the details for a particular change.',
		href: '/app/program-management/edit-history',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'History'],
		searchTerms: [
			'program changes',
			'audit history',
			'change details',
			'event history',
		],
	},
	{
		id: 'manage-program-paperwork',
		title: 'Manage program paperwork definitions',
		guidance:
			'Create or update program paperwork types, review notes history, filter archived definitions by end date, and expire or restore definitions.',
		href: '/app/program-management/paperwork',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Paperwork'],
		searchTerms: [
			'program document type',
			'paperwork type',
			'paperwork history',
			'expire paperwork',
		],
	},
	{
		id: 'manage-program-requirements',
		title: 'Manage program requirements',
		guidance:
			'Open the requirements workspace for the selected program and maintain its configured requirements.',
		href: '/app/program-management/requirements',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Programs', 'Requirements'],
		searchTerms: [
			'program requirement',
			'school requirement',
			'institute requirement',
		],
	},
	{
		id: 'manage-curriculum-courses',
		title: 'Manage curriculum courses',
		guidance:
			'Create or update courses, versions, and course sections, or activate and deactivate course records.',
		href: '/app/curriculum/courses',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Courses'],
		searchTerms: [
			'course version',
			'create course',
			'course settings',
			'course section',
		],
	},
	{
		id: 'manage-curriculum-sections',
		title: 'Manage curriculum sections',
		guidance:
			'Browse and edit LMS sections, filter by syllabus hours, refresh mappings, review teachers, activate or deactivate sections, and configure section-level settings such as No Final, Non-Credit, persistent status, parental consent, and required assignments.',
		href: '/app/curriculum/sections',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Sections'],
		searchTerms: [
			'lms section',
			'teacher section',
			'refresh section',
			'deactivate section',
			'reactivate section',
			'section no final',
			'non credit section',
			'Total Time Credit Hours',
			'persistent course',
			'parental consent',
			'required assignments',
			'required assignment pass percent',
			'passing threshold',
		],
	},
	{
		id: 'manage-course-catalogs',
		title: 'Manage course catalogs',
		guidance:
			'Create, clone, or update catalogs and add, remove, or replace their curriculum sections.',
		href: '/app/curriculum/catalogs',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Catalogs'],
		searchTerms: [
			'course catalog',
			'catalog sections',
			'clone catalog',
			'replace section',
		],
	},
	{
		id: 'manage-graduation-plans',
		title: 'Manage graduation plans',
		guidance:
			'Create, clone, or update graduation plans and maintain their subject and course requirements.',
		href: '/app/curriculum/graduation-plans',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Graduation Plans'],
		searchTerms: [
			'grad plan',
			'graduation requirement',
			'subject requirement',
			'course requirement',
		],
	},
	{
		id: 'map-section-teachers',
		title: 'Map teachers to curriculum sections',
		guidance:
			'Add, copy, edit, schedule, or deactivate teacher and grader mappings for an LMS section, and migrate affected students when needed.',
		href: '/app/curriculum/mappings/sections',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Teacher Mappings'],
		searchTerms: [
			'section teacher',
			'teacher manager',
			'copy mapping',
			'scheduled mapping change',
			'grader mapping',
			'migrate students',
		],
	},
	{
		id: 'manage-course-endorsements',
		title: 'Manage course endorsement mappings',
		guidance:
			'Choose a curriculum course and add or remove the endorsements required for its teachers.',
		href: '/app/curriculum/mappings/courses',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Endorsements'],
		searchTerms: [
			'course mapping',
			'endorsement requirement',
			'teacher qualification',
		],
	},
	{
		id: 'manage-teacher-mappings',
		title: 'Manage teacher mappings',
		guidance:
			'Edit or deactivate teacher-of-record mappings, manage graders, and migrate affected students to another mapping.',
		href: '/app/curriculum/mappings/teachers',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Teacher Mappings'],
		searchTerms: [
			'teacher manager',
			'teacher of record',
			'course teacher assignment',
			'grader mapping',
			'remove teacher mapping',
			'deactivate teacher',
			'migrate students',
		],
	},
	{
		id: 'manage-teacher-certificates',
		title: 'Manage teacher certificates',
		guidance:
			'Create, review, edit, or delete certificate records and maintain their endorsement requirements.',
		href: '/app/curriculum/mappings/certificates',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Teacher Credentials'],
		searchTerms: [
			'teacher certification',
			'certificate endorsement',
			'credential',
		],
	},
	{
		id: 'manage-endorsements',
		title: 'Manage teacher endorsements',
		guidance:
			'Create, update, or delete endorsements and review the teachers assigned to one.',
		href: '/app/curriculum/mappings/endorsements',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Curriculum', 'Teacher Credentials'],
		searchTerms: [
			'endorsement mapping',
			'teacher endorsement',
			'credential requirement',
		],
	},
	{
		id: 'manage-classrooms',
		title: 'Manage classrooms',
		guidance:
			'Create, clone, edit, or archive classroom records and open their schedules or student lists.',
		href: '/app/classroom-management',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Classrooms', 'Management'],
		searchTerms: [
			'create classroom',
			'clone classroom',
			'archive classroom',
			'classroom manager',
		],
	},
	{
		id: 'manage-classroom-sessions-schedules',
		title: 'Manage classroom sessions and schedules',
		guidance:
			'Choose a classroom to maintain sessions, courses, programs, periods, and scheduled activities.',
		href: '/app/classroom-management',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Classrooms', 'Schedules'],
		searchTerms: [
			'class session',
			'bell schedule',
			'schedule period',
			'class activity',
		],
	},
	{
		id: 'manage-classroom-students',
		title: 'Manage students in a classroom',
		guidance:
			'Choose a classroom to assign, remove, or reassign students and update courses, coaches, or work queues.',
		href: '/app/classroom-management',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Classrooms', 'Students'],
		searchTerms: [
			'class roster',
			'assign student',
			'reassign student',
			'classroom course',
			'coach',
		],
	},
	{
		id: 'manage-staff-teams',
		title: 'Manage staff teams',
		guidance:
			'Create, copy, edit, or delete teams and include or exclude their users and programs.',
		href: '/app/role-manager/teams',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Staff', 'Teams'],
		searchTerms: [
			'role manager',
			'staff group',
			'team members',
			'team programs',
		],
	},
	{
		id: 'manage-assignments-by-role',
		title: 'Manage staff assignments by role',
		guidance:
			'Choose a staff role to review assignments and add or remove special program assignments.',
		href: '/app/role-manager/assignments-by-role',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Staff', 'Assignments'],
		searchTerms: [
			'role assignment',
			'staff role',
			'special program assignment',
		],
	},
	{
		id: 'manage-assignments-by-program',
		title: 'Manage staff assignments by program',
		guidance:
			'Choose a program to review its staff assignments and add or remove special user assignments.',
		href: '/app/role-manager/assignments-by-program',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Staff', 'Assignments'],
		searchTerms: [
			'program assignment',
			'assigned staff',
			'special user assignment',
		],
	},
	{
		id: 'manage-staff-student-loads',
		title: 'Manage staff student loads',
		guidance:
			'Review a staff member’s students, then migrate, bulk reassign, or unassign selected or all students, including loads left with inactive staff.',
		href: '/app/role-manager/student-loads',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Staff', 'Student Loads'],
		searchTerms: [
			'caseload',
			'student load',
			'move student load',
			'bulk reassign students',
			'inactive staff assignments',
			'migrate students',
			'unassign students',
		],
	},
	{
		id: 'find-student-staff-assignments',
		title: 'Find or change a student’s assigned staff',
		guidance:
			'Search across staff assignments, find missing assignments, review assignment history, and update one or more students.',
		href: '/app/role-manager/student-search',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Staff', 'Assignments'],
		searchTerms: [
			'student staff',
			'assigned counselor',
			'missing staff assignment',
			'assignment history',
			'bulk assign staff',
			'bulk migrate',
		],
	},
	{
		id: 'review-focus-report',
		title: 'Review the FOCUS report',
		guidance:
			'Review FOCUS queue information, download it as CSV, or open a student’s related tracker.',
		href: '/app/role-manager/focus',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Staff', 'FOCUS'],
		searchTerms: [
			'focus queue',
			'focus csv',
			'focus export',
			'student tracker',
		],
	},
	{
		id: 'manage-role-permissions',
		title: 'Manage role permissions',
		guidance:
			'Choose a role to review, add, remove, or save its standard permissions.',
		href: '/app/permission-manager/roles',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'Permissions'],
		searchTerms: [
			'permission role',
			'standard permissions',
			'role access',
			'staff permissions',
		],
	},
	{
		id: 'manage-program-permission-overrides',
		title: 'Manage program permission overrides',
		guidance:
			'Add, revoke, or remove permission overrides for a particular program.',
		href: '/app/permission-manager/program-overrides',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'Permission Overrides'],
		searchTerms: [
			'program access',
			'permission exception',
			'override permission',
		],
	},
	{
		id: 'manage-program-type-permission-overrides',
		title: 'Manage program-type permission overrides',
		guidance:
			'Add, revoke, or remove permission overrides that apply to a program type.',
		href: '/app/permission-manager/type-overrides',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'Permission Overrides'],
		searchTerms: [
			'program type access',
			'permission exception',
			'type override',
		],
	},
	{
		id: 'review-permission-change-history',
		title: 'Review permission change history',
		guidance:
			'Review the audit history for changes made to roles, permissions, and overrides.',
		href: '/app/permission-manager/change-history',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'Permission History'],
		searchTerms: [
			'permission audit',
			'access history',
			'override history',
			'who changed permission',
		],
	},
	{
		id: 'review-user-role-history',
		title: 'Review user role history',
		guidance: 'Review when roles were added to or removed from user accounts.',
		href: '/app/permission-manager/role-change-history',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'Permission History'],
		searchTerms: ['role audit', 'user role changes', 'assigned role history'],
	},
	{
		id: 'manage-user-roles',
		title: 'Manage roles for a user',
		guidance:
			'Find a person, open their role details, and update the roles assigned to that account; roles assigned by a program remain identified and read-only here.',
		href: '/app/permission-manager/people',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'User Access'],
		searchTerms: [
			'person permissions',
			'assign role',
			'remove role',
			'locked role',
			'user access',
		],
	},
	{
		id: 'unblock-login-ip-addresses',
		title: 'Unblock a login IP address',
		guidance:
			'Review IP addresses blocked by login flood control and remove a block when permitted.',
		href: '/admin/people/flood-unblock',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'User Access'],
		searchTerms: [
			'flood unblock',
			'blocked IP',
			'unblock IP address',
			'failed login attempts',
			'login lockout',
		],
	},
	{
		id: 'manage-attendance-rulesets',
		title: 'Manage attendance rulesets',
		guidance:
			'Create, edit, or delete attendance rulesets; assign groups; configure attendance-proof rules; and maintain instructional-minute policies and limits.',
		href: '/admin/config/ga/attendance/rulesets',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'Attendance'],
		searchTerms: [
			'attendance rule',
			'attendance proof',
			'Use instructional-minute attendance',
			'Instructional-minute policy',
			'Shared policy ID',
			'Minimum daily minutes',
			'Maximum daily minutes',
			'Maximum course minutes per school year',
			'Maximum student minutes per school year',
			'OFSDP minutes',
		],
	},
	{
		id: 'send-mass-email',
		title: 'Send a mass email',
		guidance:
			'Compose and preview an email, select an eligible audience, confirm the recipient count and permission limit, and send the message.',
		href: '/app/communication/emails',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Communication', 'Email'],
		searchTerms: [
			'bulk email',
			'group email',
			'broadcast email',
			'mass communication',
			'recipient limit',
		],
	},
	{
		id: 'send-mass-sms',
		title: 'Send a mass SMS message',
		guidance:
			'Compose and preview a text message, select an eligible audience, and send it.',
		href: '/app/communication/sms',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Communication', 'SMS'],
		searchTerms: [
			'bulk text',
			'group text',
			'text message',
			'mass communication',
		],
	},
	{
		id: 'manage-announcements',
		title: 'Manage announcements',
		guidance:
			'Create, view, edit, copy, end, or delete announcements for eligible audiences.',
		href: '/app/communication/announcements',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Communication', 'Announcements'],
		searchTerms: [
			'create announcement',
			'copy announcement',
			'end announcement',
			'school notice',
		],
	},
	{
		id: 'manage-communication-groups',
		title: 'Manage communication groups',
		guidance:
			'Create, copy, edit, transfer, or delete custom groups; use program, tier, and student-status filters to build an eligible audience; and review group members or announcements.',
		href: '/app/communication/groups',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Communication', 'Groups'],
		searchTerms: [
			'custom group',
			'message audience',
			'group members',
			'group announcements',
			'program tier filter',
			'student status filter',
			'transfer group',
		],
	},
	{
		id: 'update-my-admin-profile',
		title: 'Update my Admin profile',
		guidance:
			'Open your profile to update personal and contact information, phone type and SMS capability, family-support contacts, calendar link, or profile picture.',
		href: '/app/profile',
		application: 'admin',
		audiences: ['customer', 'admin', 'superAdmin'],
		categoryPath: ['Profile', 'Personal Information'],
		searchTerms: [
			'my profile',
			'staff contact information',
			'phone number',
			'calling number',
			'texting number',
			'sms capable',
			'calendar link',
			'family support',
			'profile picture',
		],
	},
	{
		id: 'manage-my-notification-subscriptions',
		title: 'Manage my notification subscriptions',
		guidance:
			'Open your profile to update notification and report subscriptions, including weekly or monthly attendance-report emails; check settings when delivery is missing; or reset subscriptions to their defaults.',
		href: '/app/profile/notifications',
		application: 'admin',
		audiences: ['customer', 'admin', 'superAdmin'],
		categoryPath: ['Profile', 'Notifications'],
		searchTerms: [
			'email subscription',
			'report subscription',
			'report delivery',
			'assignment notification',
			'enrollment notification',
			'paperwork notification',
			'notification settings',
			'reset subscription',
			'attendance report subscription',
			'weekly attendance report',
			'monthly attendance report',
		],
	},
	{
		id: 'manage-my-account-security',
		title: 'Manage my Admin account security',
		guidance:
			'Open your profile Security tab to manage Google sign-in, login email, or password options available to you.',
		href: '/app/profile',
		application: 'admin',
		audiences: ['customer', 'admin', 'superAdmin'],
		categoryPath: ['Profile', 'Security'],
		searchTerms: [
			'change password',
			'login email',
			'google sso',
			'connect google',
			'sign in',
		],
	},
	{
		id: 'review-my-profile-activity-and-agreements',
		title: 'Review my profile activity and agreements',
		guidance:
			'Open your profile to review available activity history, tasks, events, and agreements.',
		href: '/app/profile',
		application: 'admin',
		audiences: ['customer', 'admin', 'superAdmin'],
		categoryPath: ['Profile', 'History'],
		searchTerms: [
			'account activity',
			'task history',
			'event history',
			'user agreement',
		],
	},
	{
		id: 'support-another-admin-user-account',
		title: 'Support another Admin user’s account',
		guidance:
			'Find the person in Permission Manager and open their profile to use permitted SSO, external-account, activation, or masquerade tools. Use Manage User Roles for role changes; credential changes remain self-service.',
		href: '/app/permission-manager/people',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Administration', 'User Accounts'],
		searchTerms: [
			'staff account',
			'admin sign in',
			'staff login support',
			'single sign on',
			'external account',
			'linked account',
			'deactivate user',
			'reactivate user',
			'masquerade',
			'super masquerade',
			'impersonate user',
		],
	},
] as const satisfies readonly SitemapEntry[];
